import castArray from 'lodash/castArray';
import intersection from 'lodash/intersection';
import uniq from 'lodash/uniq';

import { formatLanguageHeader } from '@lumapps/base-api';
import { get } from '@lumapps/constants';
import { getAvailableLanguages } from '@lumapps/customer/ducks/selectors';
import {
    getDefaultInstanceLanguage,
    getInstanceLanguages,
    getInstanceProperties,
} from '@lumapps/instance/ducks/selectors';
import { createSelector } from '@lumapps/redux/reselect';
import { isConnected } from '@lumapps/user/ducks/selectors';

import { DEFAULT_LANGUAGE } from '../constants';

const constants = get();

const internalLocaleSelector = (state: BaseStore) => state?.locale || {};

/**
 * Input function for the "getLanguages" selector.
 * This function is needed to retrieve the extra parameters.
 *
 * Typescript doesn't seem to be able to infer parameters when created inline within the selector, so
 * we have to create it separately.
 */
const getLanguagesDynamicParams = (_: BaseStore, useCustomerLanguages = false, languagesToInclude: string[] = []) => ({
    useCustomerLanguages,
    languagesToInclude,
});

/**
 * Retrieves the list of languages from a redux state
 * @param state Redux state
 * @param useCustomerLanguages if set to true, this function will return the languages defined
 * for the current customer. By default, it returns the list of languages defined for the current instance/site
 * @param languagesToInclude if passed in, the languages will be filtered by the list of languages passed in. if this
 * parameter is left empty, then all available languages will be used.
 */
export const getLanguages = createSelector(
    getAvailableLanguages,
    getInstanceLanguages,
    getLanguagesDynamicParams,
    (availableLangues, instanceLanguages, { useCustomerLanguages, languagesToInclude }) => {
        let languages = [];

        if (useCustomerLanguages) {
            languages = availableLangues;
        } else {
            languages = instanceLanguages || [];
        }
        return languagesToInclude.length > 0 ? intersection(languages, languagesToInclude) : languages;
    },
);

export const localeSelector = createSelector(internalLocaleSelector, (locale) => locale);

export const currentLanguageSelector = createSelector(
    localeSelector,
    (locale) => locale.current || constants.userLang || DEFAULT_LANGUAGE,
);
/**
 * Returns the current language if it exists in the instance languages list.
 * Otherwise, returns the default instance language.
 */
export const currentLanguageValidatedByInstanceLanguagesSelector = createSelector(
    localeSelector,
    getInstanceLanguages,
    getDefaultInstanceLanguage,
    (locale, instanceLanguages, defaultInstanceLanguage) =>
        (instanceLanguages?.includes(locale.current) ? locale.current : defaultInstanceLanguage) || DEFAULT_LANGUAGE,
);
export const inputLanguageSelector = createSelector(
    localeSelector,
    (locale) => locale.inputLanguage || constants.userLang || DEFAULT_LANGUAGE,
);
export const fallbackLanguageSelector = createSelector(
    localeSelector,
    (locale): string => locale.fallback || constants.userLang || DEFAULT_LANGUAGE,
);
export const contributionLanguagesSelector = createSelector(
    localeSelector,
    (locale) => locale.contributionLanguages || [DEFAULT_LANGUAGE],
);

/**
 * Whether the language switcher should be displayed for the current user
 * Note: those instance settings
 */
export const isLanguageSelectButtonEnabled = createSelector(
    getInstanceProperties,
    isConnected,
    (properties, isUserConnected) => {
        if (!isUserConnected) {
            // Note: the default value is true for unauthenticated user as it was historically the case
            // since the implementation of the language dropdown feature
            return properties.displayLanguageSelectorForUnauthenticatedUser ?? true;
        }

        return properties.displayLanguageSelector;
    },
);

/**
 * Return the formatted Accept-Language header, we need it to pass the
 * wanted language in the calls when we want to get some translated content.
 * The format of this header is the following: lang;q=X
 * where X is the weight of the previously mentioned lang, this is used to
 * classify every language. We can add more than one language, separating them by a comma.
 */
export const getLanguageHeader = createSelector(
    currentLanguageSelector,
    fallbackLanguageSelector,
    (currentLanguage, fallbackLanguage) =>
        formatLanguageHeader(uniq([currentLanguage, ...castArray(fallbackLanguage)])),
);

/**
 * Return the formatted Accept-Language header used to retrieve a content generated by a user.
 * Only get the languages that are set as contribution languages.
 */
export const getAcceptedContributionLanguageHeader = createSelector(
    currentLanguageSelector,
    fallbackLanguageSelector,
    contributionLanguagesSelector,
    getDefaultInstanceLanguage,
    (currentLanguage, fallbackLanguage, contributionLanguages, defaultInstanceLanguage) =>
        formatLanguageHeader(
            uniq([
                currentLanguage,
                ...castArray(fallbackLanguage),
                defaultInstanceLanguage,
                ...contributionLanguages,
            ]).filter((lang) => contributionLanguages.includes(lang)),
            false,
        ),
);
